<template>
  <SbPortal
    ref="portalRef"
    :target="modalTarget"
    :disabled="disabledTargetDefault"
  >
    <SbBlokUi
      v-if="open"
      v-focus-trap="focusTrapConfig"
      class="sb-modal__sb-block-ui"
      :style="computedBlokUiStyle"
      :full-width="fullWidth"
      @mousedown="wrapClose"
    >
      <dialog
        :id="id"
        ref="modal"
        class="sb-modal"
        :class="computedClasses"
        :style="computedStyle"
        v-bind="attrs"
      >
        <SbModalCloseButton
          v-if="showClose"
          :class="classes?.closeButton"
          :data-testid="closeButtonDataTestId"
          @click="handleCloseModal"
        />
        <slot />
      </dialog>
    </SbBlokUi>
  </SbPortal>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  nextTick,
  onBeforeUnmount,
  useAttrs,
  onMounted,
} from 'vue'
import type { CSSProperties } from 'vue'
import SbBlokUi from '../BlockUI'
import SbPortal from '../Portal'
import { randomString } from '../../utils'
import { FocusTrap as vFocusTrap } from '../../directives'

import SbModalCloseButton from './components/SbModalCloseButton.vue'

// Type guard to check if the target is a Node
function isNode(target: EventTarget | null): target is Node {
  return target instanceof Node
}

export type SbModalProps = {
  classes?: {
    closeButton?: string
  }
  closeOnClickOutside?: boolean
  closeOnHeader?: boolean
  customClass?: string
  disabledTargetDefault?: boolean
  enableInitialFocus?: boolean
  escCloses?: boolean
  escapeBlockingSelectors?: string[]
  fullWidth?: boolean
  hasFocusTrap?: boolean
  id?: string | undefined
  isOpen?: boolean
  large?: boolean
  maxWidth?: string | number
  modalTarget?: string
  overlayPosition?: 'fixed' | 'absolute' | 'sticky' | 'relative' | 'static'
  overlayTransparent?: boolean
  scrollbar?: boolean
  showClose?: boolean
}

export type SbModalEmits = {
  (e: 'hide'): void
  (e: 'show'): void
}

const props = withDefaults(defineProps<SbModalProps>(), {
  closeOnClickOutside: true,
  closeOnHeader: false,
  customClass: '',
  disabledTargetDefault: false,
  enableInitialFocus: true,
  escCloses: true,
  escapeBlockingSelectors: () => [],
  fullWidth: false,
  hasFocusTrap: true,
  id: undefined,
  isOpen: false,
  large: false,
  maxWidth: undefined,
  modalTarget: () => `#sb-modal-target-${randomString(4)}`,
  overlayPosition: 'fixed',
  overlayTransparent: false,
  scrollbar: false,
  showClose: true,
})

const emit = defineEmits<SbModalEmits>()

const attrs = useAttrs()

const open = ref(false)
const modal = ref<HTMLDialogElement | null>(null)
const portalRef = ref<{ mountTarget?: () => void } | null>(null)

const closeButtonDataTestId = computed(() => {
  const dataTestid = attrs['data-testid']
  return dataTestid ? `${dataTestid}-close-button` : 'sb-modal-close-button'
})

const computedClasses = computed(() => {
  return [
    props.customClass?.length && props.customClass,
    props.fullWidth && 'sb-modal__full-width',
    props.large && 'sb-modal__large',
    props.scrollbar && 'sb-modal--scrollbar',
    props.closeOnHeader && 'sb-modal--close-on-header',
  ]
})

const computedBlokUiStyle = computed(() => {
  const style: CSSProperties = { position: props.overlayPosition ?? 'fixed' }

  if (props.overlayTransparent) {
    style.background = 'none'
  }

  return { ...style }
})

const computedStyle = computed(() => {
  if (!props.maxWidth) {
    return null
  }

  return { maxWidth: `${props.maxWidth}px` }
})

const focusTrapConfig = computed(() => {
  return {
    enabled: props.hasFocusTrap && open.value,
    enableInitialFocus: props.enableInitialFocus,
  }
})
function escapeEventListener(e: KeyboardEvent): void {
  if (e.key === 'Escape' && props.escCloses) {
    for (const selector of props.escapeBlockingSelectors) {
      const elements = document.querySelectorAll(selector)
      for (const element of elements) {
        const style = window.getComputedStyle(element)
        if (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0'
        ) {
          return
        }
      }
    }

    handleCloseModal()
  }
}

function removeListener(): void {
  window.removeEventListener('keydown', escapeEventListener, true)
}

function registerListener(): void {
  window.addEventListener('keydown', escapeEventListener, true)
}

/**
 * handler for closing the modal by pressing ESC on the keyboard
 */
function handleCloseModalByPressingEsc(): void {
  if (props.escCloses) {
    registerListener()
  }
}

/**
 * handler for close modal
 */
function handleCloseModal(): void {
  if (open.value) {
    removeListener()
    open.value = false
    nextTick(() => {
      const body = document.querySelector('body')
      if (body) {
        body.style.overflow = 'auto'
      }
      emit('hide')
    })
  }
}

/**
 * handler for open modal
 */
function handleOpenModal(): void {
  if (!open.value) {
    handleCloseModalByPressingEsc()
    open.value = true
    nextTick(() => {
      const body = document.querySelector('body')
      if (body) {
        body.style.overflow = 'hidden'
      }
      emit('show')
    })
  }
}

function wrapClose(event: MouseEvent): void {
  if (
    modal.value &&
    isNode(event.target) &&
    !modal.value.contains(event.target) &&
    props.closeOnClickOutside
  ) {
    handleCloseModal()
  }
}

function hideOverflow(): void {
  const body = document.querySelector('body')
  if (body) {
    body.style.overflow = 'hidden'
  }
}

/**
 * function to create the portal instance
 */
function createPortalInstance(): void {
  if (portalRef.value && typeof portalRef.value.mountTarget === 'function') {
    portalRef.value.mountTarget()
  }
}

watch(
  () => props.isOpen,
  (value) => {
    if (value) {
      handleOpenModal()
    } else {
      handleCloseModal()
    }
  },
  { immediate: true },
)

onMounted(() => {
  createPortalInstance()
})

onBeforeUnmount(() => {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  if (document.querySelector) {
    const body = document.querySelector('body')
    if (body) {
      body.style.overflow = 'auto'
    }
  }
})

defineExpose({
  hideOverflow,
})
</script>
